import {
  announceAddedToCart,
  formatMoney
} from "./chunk-66g4vxwm.js";
import {
  showCartError
} from "./chunk-9v5gng0d.js";
import {
  readJsonScript,
  isRecord,
  cart
} from "./chunk-jwpq5e39.js";
import {
  refreshCartChrome,
  openCartDrawer
} from "./chunk-ck42pzrv.js";

// src/components/product-grid-v2/index.ts
var PAYMENT_TYPES = [
  "ots",
  "ots_full",
  "ots_installment",
  "pay_per_month",
  "pay_per_shipment"
];
var isPaymentType = (value) => PAYMENT_TYPES.some((paymentType) => paymentType === value);
var CARD_SELECTOR = "[data-product-grid-v2-card]";
var PICKER_SELECTOR = "[data-product-grid-v2-picker]";
var SELECT_SELECTOR = "[data-product-grid-v2-select]";
var FILTER_GROUP_SELECTOR = "input[data-product-grid-v2-filter-group]";
var FILTER_ROOT_SELECTOR = "[data-product-grid-v2-filter-root]";
var MOBILE_FILTERS_QUERY = "(max-width: 1023px)";
var SHEET_TRANSITION_MS = 300;
var CHIP_CLOSE_ICON = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect width="16" height="16" rx="8" fill="#E2C5EB"/><path fill-rule="evenodd" clip-rule="evenodd" d="M8.00033 7.40579L9.36636 6.03976C9.53045 5.87566 9.7965 5.87566 9.96059 6.03976C10.1247 6.20385 10.1247 6.4699 9.96059 6.63399L8.59456 8.00002L9.96059 9.36605C10.1247 9.53014 10.1247 9.79619 9.96059 9.96028C9.7965 10.1244 9.53045 10.1244 9.36636 9.96028L8.00033 8.59425L6.63429 9.96028C6.4702 10.1244 6.20415 10.1244 6.04006 9.96028C5.87597 9.79619 5.87597 9.53014 6.04006 9.36605L7.40609 8.00002L6.04006 6.63399C5.87597 6.4699 5.87597 6.20385 6.04006 6.03976C6.20415 5.87566 6.4702 5.87566 6.63429 6.03976L8.00033 7.40579Z" fill="#5E2C6D"/></svg>';
var isMobileFiltersSheet = () => window.matchMedia(MOBILE_FILTERS_QUERY).matches;
var formattedMonthlyBase = (raw) => {
  const value = raw.trim();
  if (value === "")
    return null;
  if (/^\d+\.?\d*$/.test(value)) {
    const amount = Number(value);
    if (Number.isFinite(amount))
      return amount.toFixed(2);
  }
  return value;
};
var monthlyBaseFor = (raw, sku, subscription, poland) => {
  const formatted = formattedMonthlyBase(raw);
  if (!poland)
    return formatted;
  if (!subscription || formatted === null)
    return null;
  if (sku.split("_")[0] !== "inst")
    return "";
  return formatted;
};
var handleize = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
var tokensFrom = (value) => (value ?? "").toLowerCase().split(/\s+/).filter(Boolean);
var tokenMatches = (tokens, value, label) => {
  const needle = value.toLowerCase();
  if (needle === "")
    return false;
  const labelNeedle = handleize(label);
  return tokens.some((token) => token === needle || labelNeedle !== "" && token === labelNeedle || token.endsWith(`-${needle}`) || labelNeedle !== "" && token.endsWith(`-${labelNeedle}`));
};
var anotherFiltersSheetOpen = (except) => [...document.querySelectorAll(".plp.plp--filters-open")].some((root) => root !== except);
var integerAttribute = (element, name) => {
  const parsed = Number(element.getAttribute(name));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};
var selectedOption = (select) => select.selectedIndex < 0 ? null : select.options.item(select.selectedIndex);
var activePicker = (card) => [...card.querySelectorAll(PICKER_SELECTOR)].find((picker) => !picker.hidden) ?? null;
var cardSupportsPaymentType = (card, paymentType) => {
  const pickers = [...card.querySelectorAll(PICKER_SELECTOR)];
  if (pickers.length === 0)
    return true;
  const picker = pickers.find((item) => item.dataset.paymentType === paymentType);
  if (picker === undefined)
    return false;
  const select = picker.querySelector(SELECT_SELECTOR);
  return select !== null && select.options.length > 0;
};
var escapeHtml = (value) => value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
var globalIdFor = (card, variantId) => {
  const value = readJsonScript(card, "[data-variant-global-ids]");
  if (!isRecord(value))
    return "";
  const variant = value[String(variantId)];
  if (!isRecord(variant))
    return "";
  const globalId = variant["globalId"];
  return typeof globalId === "string" ? globalId : "";
};
var isManualPayment = () => {
  const scope = window;
  if (!isRecord(scope))
    return false;
  const krown = scope["KROWN"];
  const settings = isRecord(krown) ? krown["settings"] : null;
  const manualTypes = isRecord(settings) ? settings["manualPaymentTypes"] : null;
  if (!Array.isArray(manualTypes))
    return false;
  const cartData = scope["cartDataForPaymentSelectionInCart"];
  const attributes = isRecord(cartData) ? cartData["attributes"] : null;
  const selectedAttribute = isRecord(attributes) ? attributes["_paymentMethod"] : null;
  const availableMethods = scope["cartAvailablePaymentMethods"];
  const subscriptionMethods = isRecord(availableMethods) ? availableMethods["subscription"] : null;
  const firstAvailable = Array.isArray(subscriptionMethods) ? subscriptionMethods[0] : null;
  const selected = typeof selectedAttribute === "string" ? selectedAttribute : typeof firstAvailable === "string" ? firstAvailable : "Online";
  return manualTypes.some((type) => type === selected);
};
var installmentFeeLine = async (manual) => {
  const build = window.buildInstallmentFeeItem;
  if (typeof build !== "function")
    return null;
  const fee = build(manual);
  if (fee === null)
    return null;
  try {
    const service = window.JPCartService;
    const peeked = service?.peek();
    const current = isRecord(peeked) && Array.isArray(peeked["items"]) ? peeked : await service?.getCart().catch(() => null);
    if (current !== null && current !== undefined && window.cartHasInstallmentFee?.(current) === true) {
      return null;
    }
  } catch {}
  const sellingPlan = fee.selling_plan ?? "";
  return {
    variantId: fee.id,
    quantity: fee.quantity,
    sellingPlanId: sellingPlan === "" ? null : sellingPlan,
    properties: fee.properties ?? {},
    manualPayment: false
  };
};

class ProductGridV2 extends HTMLElement {
  abortController = null;
  busy = new WeakSet;
  appliedFilterState = null;
  closingFiltersFromApply = false;
  lastActiveFacetsHtml = "";
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController;
    const signal = this.abortController.signal;
    this.addEventListener("change", this.handleChange, { signal });
    this.addEventListener("click", this.handleClick, { signal });
    document.addEventListener("click", this.handleDocumentClick, { signal });
    document.addEventListener("keydown", this.handleDocumentKeydown, { signal });
    const onViewportChange = () => this.syncPromoTile();
    window.matchMedia("(min-width: 1024px)").addEventListener("change", onViewportChange, { signal });
    window.matchMedia("(max-width: 767px)").addEventListener("change", onViewportChange, { signal });
    this.applyPaymentType(this.defaultPaymentType());
    if (this.filterRoot() !== null) {
      this.appliedFilterState = this.captureFilterState();
    }
    this.syncPromoTile();
  }
  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }
  defaultPaymentType() {
    if (this.dataset.purchaseType === "ots" && this.dataset.otsPaymentChoices === "true") {
      return this.dataset.defaultPaymentType === "ots_installment" ? "ots_installment" : "ots_full";
    }
    if (this.dataset.purchaseType !== "subscription")
      return "ots";
    return this.dataset.defaultPaymentType === "pay_per_shipment" ? "pay_per_shipment" : "pay_per_month";
  }
  handleChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches("[data-product-grid-v2-payment-toggle] input")) {
      if (input.checked)
        this.applyPaymentType(this.paymentType(input.value));
      return;
    }
    if (input instanceof HTMLInputElement && input.matches(FILTER_GROUP_SELECTOR)) {
      this.handleFilterDraftChange();
      return;
    }
    if (!(input instanceof HTMLSelectElement) || !input.matches(SELECT_SELECTOR))
      return;
    const card = input.closest(CARD_SELECTOR);
    if (card === null)
      return;
    this.updateCard(card);
  };
  openTerms() {
    const dialog = this.querySelector("[data-terms-dialog]");
    if (dialog === null || dialog.open)
      return;
    if (dialog.dataset.termsBound !== "true") {
      dialog.dataset.termsBound = "true";
      dialog.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element))
          return;
        if (target === dialog || target.closest("[data-close]"))
          dialog.close();
      });
    }
    const home = dialog.parentElement;
    document.body.append(dialog);
    document.body.style.overflow = "hidden";
    dialog.showModal();
    dialog.addEventListener("close", () => {
      document.body.style.overflow = "";
      if (home !== null)
        home.append(dialog);
    }, { once: true });
    dialog.querySelector("[data-close]")?.focus();
  }
  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target))
      return;
    if (target.closest("[data-product-grid-v2-filters-toggle]")) {
      event.preventDefault();
      this.setFiltersOpen(!this.filtersAreOpen());
      return;
    }
    if (target.closest("[data-product-grid-v2-filters-close]")) {
      event.preventDefault();
      if (isMobileFiltersSheet())
        this.setFiltersOpen(false);
      return;
    }
    if (target.closest("[data-product-grid-v2-filters-backdrop]")) {
      event.preventDefault();
      if (isMobileFiltersSheet())
        this.setFiltersOpen(false);
      return;
    }
    if (target.closest("[data-product-grid-v2-filters-apply]")) {
      event.preventDefault();
      this.applyFilters();
      this.appliedFilterState = this.captureFilterState();
      if (!isMobileFiltersSheet())
        return;
      this.closingFiltersFromApply = true;
      this.setFiltersOpen(false);
      return;
    }
    if (target.closest("[data-product-grid-v2-filters-clear]")) {
      event.preventDefault();
      this.clearFilters();
      return;
    }
    const removeChip = target.closest("[data-product-grid-v2-remove-filter]");
    if (removeChip !== null) {
      event.preventDefault();
      this.removeFilterChip(removeChip.dataset.filterGroup ?? "", removeChip.dataset.filterValue ?? "");
      return;
    }
    if (target.closest("[data-product-grid-v2-clear-all-filters]")) {
      event.preventDefault();
      this.clearFilters();
      return;
    }
    if (target.closest("[data-product-grid-v2-terms]")) {
      event.preventDefault();
      this.openTerms();
      return;
    }
    const button = target.closest("[data-product-grid-v2-add]");
    if (button === null)
      return;
    event.preventDefault();
    this.addToCart(button);
  };
  handleDocumentClick = (event) => {
    if (!isMobileFiltersSheet() || !this.filtersAreOpen())
      return;
    const target = event.target;
    if (!(target instanceof Element))
      return;
    if (this.filterPanel()?.contains(target))
      return;
    const toggle = this.querySelector("[data-product-grid-v2-filters-toggle]");
    if (toggle?.contains(target))
      return;
    this.setFiltersOpen(false);
  };
  handleDocumentKeydown = (event) => {
    if (event.key !== "Escape")
      return;
    if (!isMobileFiltersSheet() || !this.filtersAreOpen())
      return;
    this.setFiltersOpen(false);
  };
  paymentType(value) {
    return isPaymentType(value) ? value : "pay_per_month";
  }
  applyPaymentType(paymentType) {
    for (const input of this.querySelectorAll("[data-product-grid-v2-payment-toggle] input")) {
      input.checked = input.value === paymentType;
      const option = input.closest(".product-grid-v2__payment-option");
      option?.classList.toggle("product-grid-v2__payment-option--selected", input.checked);
      option?.classList.toggle("button--outline", input.checked);
    }
    for (const card of this.querySelectorAll(CARD_SELECTOR)) {
      card.dataset.paymentType = paymentType;
      for (const picker of card.querySelectorAll(PICKER_SELECTOR)) {
        picker.hidden = picker.dataset.paymentType !== paymentType;
      }
      this.updateCard(card);
    }
    this.syncCardVisibility();
  }
  currentPaymentType() {
    const checked = this.querySelector("[data-product-grid-v2-payment-toggle] input:checked");
    if (checked !== null)
      return this.paymentType(checked.value);
    return this.defaultPaymentType();
  }
  syncCardVisibility() {
    if (this.filterRoot() !== null) {
      this.applyFilters();
      return;
    }
    const paymentType = this.currentPaymentType();
    let visible = 0;
    for (const card of this.querySelectorAll(CARD_SELECTOR)) {
      const show = cardSupportsPaymentType(card, paymentType);
      card.hidden = !show;
      if (show)
        visible += 1;
    }
    this.syncPromoTile(visible);
  }
  updateCard(card) {
    const picker = activePicker(card);
    const select = picker?.querySelector(SELECT_SELECTOR) ?? null;
    const option = select === null ? null : selectedOption(select);
    const button = card.querySelector("[data-product-grid-v2-add]");
    const price = card.querySelector("[data-product-grid-v2-price]");
    const frequency = card.querySelector("[data-product-grid-v2-price-frequency]");
    if (option !== null && price !== null) {
      const cents = Number(option.dataset.price);
      if (Number.isFinite(cents)) {
        price.textContent = formatMoney(cents, this.dataset.moneyFormat ?? "${{amount}}");
      }
    }
    this.updatePromoLine(card, option);
    if (frequency !== null) {
      frequency.textContent = card.dataset.paymentType === "pay_per_month" ? frequency.dataset.monthLabel ?? "" : frequency.dataset.shipmentLabel ?? "";
    }
    if (button === null)
      return;
    const available = option?.dataset.available === "true";
    button.disabled = !available;
    button.setAttribute("aria-disabled", String(!available));
  }
  updatePromoLine(card, option) {
    const promo = card.querySelector("[data-product-grid-v2-promo]");
    if (promo === null)
      return;
    const template = promo.dataset.promoTemplate ?? "";
    if (template === "")
      return;
    let html = escapeHtml(template);
    const needsPrice = template.includes("{price}");
    const needsProduct = template.includes("{product}");
    if (needsPrice) {
      const percent = Number(promo.dataset.promoPercent);
      const cents = Number(option?.dataset.price);
      if (!Number.isFinite(percent) || percent < 0 || percent > 100 || !Number.isFinite(cents)) {
        promo.hidden = true;
        return;
      }
      const discounted = Math.round(cents * (100 - percent) / 100);
      const money = formatMoney(discounted, this.dataset.moneyFormat ?? "${{amount}}");
      const priceHtml = `<span class="product-grid-v2__promo-price">${escapeHtml(money)}</span>`;
      html = html.replaceAll("{{price}}", priceHtml).replaceAll("{price}", priceHtml);
    }
    if (needsProduct) {
      const name = option?.dataset.freeProduct ?? "";
      if (name === "") {
        promo.hidden = true;
        return;
      }
      const nameHtml = escapeHtml(name);
      html = html.replaceAll("{{product}}", nameHtml).replaceAll("{product}", nameHtml);
    }
    promo.hidden = false;
    promo.innerHTML = html;
  }
  async addToCart(button) {
    if (button.disabled || this.busy.has(button))
      return;
    const card = button.closest(CARD_SELECTOR);
    const picker = card === null ? null : activePicker(card);
    const select = picker?.querySelector(SELECT_SELECTOR) ?? null;
    const option = select === null ? null : selectedOption(select);
    if (card === null || option === null)
      return;
    const variantId = integerAttribute(option, "value");
    const planId = integerAttribute(option, "data-plan-id");
    if (variantId === null)
      return;
    const paymentType = card.dataset.paymentType ?? "ots";
    const isOtsInstallment = paymentType === "ots_installment";
    const isOneTimeFull = paymentType === "ots" || paymentType === "ots_full";
    const payPerMonth = paymentType === "pay_per_month";
    const manual = !isOneTimeFull && isManualPayment();
    const planName = isOtsInstallment ? this.dataset.otsSellingPlanName ?? "" : option.getAttribute("data-plan-name") ?? "";
    const subscriptionType = isOneTimeFull ? "" : payPerMonth || isOtsInstallment ? "installment" : "normal";
    const subscription = paymentType === "pay_per_month" || paymentType === "pay_per_shipment";
    const monthlyBase = monthlyBaseFor(option.getAttribute("data-monthly-base") ?? "", option.getAttribute("data-sku") ?? "", subscription, this.dataset.polandFee === "true");
    const properties = {
      _globalId: globalIdFor(card, variantId),
      _discountvalue: option.getAttribute("data-discount-value") ?? "",
      _selling_plan_name: planName,
      _sellingplan: "",
      _ots_installment: isOtsInstallment ? "true" : "false",
      _subscriptionType: subscriptionType
    };
    if (monthlyBase !== null)
      properties["_monthly_base_total"] = monthlyBase;
    this.setLoading(button, true);
    const lines = [
      {
        variantId,
        quantity: 1,
        sellingPlanId: isOneTimeFull ? null : planId,
        properties,
        manualPayment: manual
      }
    ];
    if (payPerMonth) {
      const fee = await installmentFeeLine(manual);
      if (fee !== null)
        lines.unshift(fee);
    }
    const result = await cart.add(lines);
    this.setLoading(button, false);
    if (!result.ok) {
      showCartError(result);
      openCartDrawer({ force: true });
      return;
    }
    if (payPerMonth)
      await window.ensureInstallmentFeeQuantityOne?.(manual);
    announceAddedToCart();
    await refreshCartChrome();
    openCartDrawer();
  }
  setLoading(button, loading) {
    if (loading) {
      this.busy.add(button);
    } else {
      this.busy.delete(button);
    }
    button.disabled = loading;
    button.setAttribute("aria-busy", String(loading));
    button.textContent = loading ? button.dataset.addingLabel ?? "" : button.dataset.addLabel ?? "";
  }
  filterRoot() {
    return this.querySelector(FILTER_ROOT_SELECTOR);
  }
  filterPanel() {
    return this.querySelector("[data-product-grid-v2-filters]");
  }
  filterCards() {
    return [...this.querySelectorAll(CARD_SELECTOR)];
  }
  filtersAreOpen() {
    return this.filterRoot()?.classList.contains("plp--filters-open") ?? false;
  }
  filterCheckboxes() {
    return [...this.querySelectorAll(FILTER_GROUP_SELECTOR)];
  }
  selectedFormatValues() {
    return this.filterCheckboxes().filter((input) => input.dataset.productGridV2FilterGroup === "format" && input.checked).map((input) => input.value);
  }
  hasActiveFilters() {
    return this.filterCheckboxes().some((input) => input.checked);
  }
  cardMatchesFormat(card, values) {
    if (values.length === 0)
      return true;
    const tokens = tokensFrom(card.dataset.plpFormat);
    return values.some((value) => {
      const input = this.filterCheckboxes().find((item) => item.dataset.productGridV2FilterGroup === "format" && item.value === value);
      return tokenMatches(tokens, value, input?.dataset.productGridV2FilterLabel ?? "");
    });
  }
  syncFilterCounts() {
    const paymentType = this.currentPaymentType();
    for (const input of this.filterCheckboxes()) {
      const countEl = input.closest("li")?.querySelector("[data-product-grid-v2-filter-count]");
      if (countEl === null || countEl === undefined)
        continue;
      if (input.dataset.productGridV2FilterGroup !== "format")
        continue;
      let count = 0;
      for (const card of this.filterCards()) {
        if (!cardSupportsPaymentType(card, paymentType))
          continue;
        if (!this.cardMatchesFormat(card, [input.value]))
          continue;
        count += 1;
      }
      countEl.textContent = String(count);
    }
  }
  applyFilters() {
    if (this.filterRoot() === null)
      return;
    const paymentType = this.currentPaymentType();
    let visible = 0;
    for (const card of this.filterCards()) {
      const matches = this.cardMatchesFormat(card, this.selectedFormatValues()) && cardSupportsPaymentType(card, paymentType);
      card.hidden = !matches;
      if (matches)
        visible += 1;
    }
    this.syncPromoTile(visible);
    this.syncFilterUi(visible);
  }
  handleFilterDraftChange() {
    if (isMobileFiltersSheet() && this.filtersAreOpen()) {
      this.syncFilterUi();
      return;
    }
    this.applyFilters();
    this.appliedFilterState = this.captureFilterState();
  }
  clearFilters() {
    for (const input of this.filterCheckboxes())
      input.checked = false;
    this.applyFilters();
    this.appliedFilterState = this.captureFilterState();
  }
  removeFilterChip(group, value) {
    const input = this.filterCheckboxes().find((item) => item.dataset.productGridV2FilterGroup === group && item.value === value);
    if (input !== undefined)
      input.checked = false;
    this.applyFilters();
    this.appliedFilterState = this.captureFilterState();
  }
  captureFilterState() {
    return {
      checkboxes: this.filterCheckboxes().map((input) => ({
        group: input.dataset.productGridV2FilterGroup ?? "",
        value: input.value,
        checked: input.checked
      }))
    };
  }
  restoreFilterState(state) {
    if (state === null)
      return;
    for (const input of this.filterCheckboxes()) {
      const match = state.checkboxes.find((item) => item.group === input.dataset.productGridV2FilterGroup && item.value === input.value);
      if (match !== undefined)
        input.checked = match.checked;
    }
  }
  buildFilterChips() {
    const chips = [];
    for (const input of this.filterCheckboxes()) {
      if (!input.checked)
        continue;
      chips.push({
        group: input.dataset.productGridV2FilterGroup ?? "",
        value: input.value,
        label: input.dataset.productGridV2FilterLabel || input.value
      });
    }
    return chips;
  }
  syncFilterUi(visibleCount) {
    const root = this.filterRoot();
    if (root === null)
      return;
    const chips = this.buildFilterChips();
    const countEl = this.querySelector("[data-product-grid-v2-filters-count]");
    if (countEl !== null)
      countEl.textContent = chips.length > 0 ? ` (${chips.length})` : "";
    const visible = visibleCount ?? this.filterCards().filter((card) => !card.hidden).length;
    this.syncProductsCount(visible);
    this.syncFilterCounts();
    this.syncActiveFacets(chips);
    this.syncEmptyState(visible);
  }
  syncProductsCount(visible) {
    const countEl = this.querySelector("[data-product-grid-v2-products-count]");
    const root = this.filterRoot();
    if (countEl === null || root === null)
      return;
    const template = visible === 1 ? root.dataset.countOne : root.dataset.countOther;
    countEl.textContent = (template ?? "").replaceAll("__COUNT__", String(visible));
  }
  syncActiveFacets(chips) {
    const host = this.querySelector("[data-product-grid-v2-active-filters]");
    const root = this.filterRoot();
    if (host === null || root === null)
      return;
    if (chips.length === 0) {
      if (this.lastActiveFacetsHtml !== "") {
        this.lastActiveFacetsHtml = "";
        host.innerHTML = "";
      }
      host.hidden = true;
      return;
    }
    const clearLabel = root.dataset.clearLabel ?? "Clear filters";
    const pills = chips.map((chip) => `<button type="button" class="plp__filter-chip" data-product-grid-v2-remove-filter data-filter-group="${escapeHtml(chip.group)}" data-filter-value="${escapeHtml(chip.value)}">
            <span class="plp__filter-chip-label">${escapeHtml(chip.label)}</span>
            ${CHIP_CLOSE_ICON}
          </button>`).join("");
    const nextHtml = `${pills}<button type="button" class="plp__filter-chips-clear" data-product-grid-v2-clear-all-filters>${escapeHtml(clearLabel)}</button>`;
    if (nextHtml === this.lastActiveFacetsHtml) {
      host.hidden = false;
      return;
    }
    this.lastActiveFacetsHtml = nextHtml;
    host.hidden = false;
    host.innerHTML = nextHtml;
  }
  syncPromoTile = (visibleCount) => {
    const promo = this.querySelector("aside.product-grid-v2__promo");
    if (promo === null)
      return;
    const visible = typeof visibleCount === "number" ? visibleCount : this.filterCards().filter((card) => !card.hidden).length;
    if (visible === 0) {
      promo.hidden = true;
      return;
    }
    if (window.matchMedia("(max-width: 767px)").matches) {
      promo.hidden = false;
      return;
    }
    const columns = window.matchMedia("(min-width: 1024px)").matches ? this.filtersAreOpen() ? 3 : 4 : 2;
    const leftover = visible % columns;
    if (leftover === 0) {
      promo.hidden = true;
      return;
    }
    promo.hidden = false;
    promo.style.setProperty(window.matchMedia("(min-width: 1024px)").matches ? "--product-grid-v2-promo-span-desktop" : "--product-grid-v2-promo-span-tablet", String(columns - leftover));
  };
  syncEmptyState(visible) {
    const root = this.filterRoot();
    const empty = this.querySelector("[data-product-grid-v2-empty-state]");
    if (root === null || empty === null)
      return;
    const isFilterEmpty = this.hasActiveFilters() && visible === 0;
    empty.hidden = !isFilterEmpty;
    root.classList.toggle("plp--filter-empty", isFilterEmpty);
  }
  setFiltersOpen(open) {
    const root = this.filterRoot();
    const panel = this.filterPanel();
    const toggle = this.querySelector("[data-product-grid-v2-filters-toggle]");
    const backdrop = this.querySelector("[data-product-grid-v2-filters-backdrop]");
    if (root === null || panel === null)
      return;
    const mobile = isMobileFiltersSheet();
    toggle?.setAttribute("aria-expanded", open ? "true" : "false");
    const overlay = document.querySelector(".site-overlay");
    const finishClose = () => {
      root.classList.remove("plp--filters-open", "plp-filters-sheet-visible");
      panel.hidden = true;
      if (backdrop !== null)
        backdrop.hidden = true;
      if (!anotherFiltersSheetOpen(root)) {
        document.body.classList.remove("plp-filters-sheet-open");
        overlay?.classList.remove("plp-filters-overlay-active");
        if (overlay !== null && !document.body.classList.contains("sidebar-opened")) {
          overlay.style.display = "none";
        }
      }
    };
    if (!open) {
      root.classList.remove("plp-filters-sheet-visible");
      if (mobile && !this.closingFiltersFromApply) {
        this.restoreFilterState(this.appliedFilterState);
        this.syncFilterUi();
      }
      this.closingFiltersFromApply = false;
      if (mobile) {
        window.setTimeout(() => {
          finishClose();
          this.syncPromoTile();
        }, SHEET_TRANSITION_MS);
      } else {
        finishClose();
        this.syncPromoTile();
      }
      return;
    }
    root.classList.add("plp--filters-open");
    this.syncPromoTile();
    if (mobile) {
      this.restoreFilterState(this.appliedFilterState);
      this.syncFilterUi();
    }
    if (backdrop !== null)
      backdrop.hidden = !mobile;
    panel.hidden = false;
    document.body.classList.toggle("plp-filters-sheet-open", mobile);
    if (mobile && overlay !== null) {
      overlay.style.display = "block";
      overlay.classList.add("plp-filters-overlay-active");
    }
    if (mobile) {
      requestAnimationFrame(() => root.classList.add("plp-filters-sheet-visible"));
    } else {
      root.classList.add("plp-filters-sheet-visible");
    }
  }
}
if (customElements.get("product-grid-v2") === undefined) {
  customElements.define("product-grid-v2", ProductGridV2);
}
